import { useGSAP } from '@gsap/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useContext, useRef } from 'react';
import { OpenTermsModalContext } from '../../../contexts/OpenTermsModalContext';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import companyLogo_green from '../../../images/companyLogo_green.svg';
import companyLogo_purple from '../../../images/companyLogo_purple.svg';
import * as serializersStyles from '../../../serializers.module.scss';
import { BackgroundColorTheme, ModuleBgColor } from '../../../templates/Page';
import { RawPortableText } from '../../../types/types';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import FileRow from '../../ui/FileRow';
import * as fileRowStyles from '../../ui/FileRow.module.scss';
import Arc from '../Arc';
import GenericPortableText from '../GenericPortableText';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import * as styles from './TextAndImageModule.module.scss';

export const TextAndImageModuleFragment = graphql`
  fragment TextAndImageModule on SanityPageTextAndImageModule {
    title {
      ...LocaleStringFragment
    }
    text {
      _type
      _rawPt(resolveReferences: { maxDepth: 4 })
      _rawEn(resolveReferences: { maxDepth: 4 })
    }
    image {
      ...SanityImage
    }
    addCompanyLogoToImage
    imageOrientation
    imageSide
    backgroundColor
  }
  {
    allSanityAreaOfExpertise(sort: { orderRank: ASC }) {
      nodes {
        ...AreaOfExpertise
      }
    }
  }
`;

export type TextAndImageModuleProps = {
  backgroundColor: BackgroundColorTheme;
  title?: string;
  text?: RawPortableText;
  fileRows?: Array<{
    name: string;
    description?: string;
    file?: {
      asset: {
        url: string;
        size: number;
      };
    };
    link?: string;
  }>;
  image: SanityImageType;
  addCompanyLogoToImage?: boolean;
  imageOrientation: 'portrait' | 'landscape';
  imageSide: 'left' | 'right';
  className?: string;
};

export function getModuleBgColor(props: TextAndImageModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'colored';
}

function TextAndImageModule(
  props: TextAndImageModuleProps & CommonModuleProps,
): React.ReactElement {
  const {
    image,
    addCompanyLogoToImage,
    title,
    text,
    fileRows,
    imageOrientation,
    imageSide,
    backgroundColor,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;

  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      const AnimationTl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentContainer}`,
          start: 'top 85%',
        } satisfies ScrollTrigger.Vars,
      });

      AnimationTl.from(`.${styles.mediaContainer}`, {
        ...(imageOrientation === 'landscape'
          ? {
              x: imageSide === 'left' ? '-100%' : '100%',
            }
          : { y: '100%' }),

        duration: 1,
        ease: 'circ.out',
      }).from(
        '.' + styles.title,
        {
          duration: 0.4,
          autoAlpha: 0,
          y: 50,
        },
        '<',
      );
      if (text) {
        if (document.querySelector(`.${styles.text}`)) {
          AnimationTl.from(
            `.${styles.text}`,
            {
              opacity: 0,
              y: 50,
              duration: 0.8,
            },
            '<',
          );
        }
        if (document.querySelector(`.${serializersStyles.listItem}`)) {
          AnimationTl.from(
            `.${serializersStyles.listItem}`,
            {
              opacity: 0,
              y: 50,
              duration: 2,
              stagger: 0.2,
            },
            '<',
          );
        }
      }
      if (document.querySelector(`.${fileRowStyles.file}`)) {
        AnimationTl.from(
          `.${fileRowStyles.file}`,
          {
            opacity: 0,
            y: 50,
            duration: 1,
            stagger: 0.2,
          },
          '<',
        );
      }
    },
    { scope: containerRef },
  );

  const openModalIfTermsNotAccepted = useContext(OpenTermsModalContext);

  return (
    <ModuleLayout
      id={moduleId}
      moduleRef={containerRef}
      className={clsx(
        className,
        styles.container,
        backgroundColor === '#E5F8E8' ? styles.greenBackground : styles.purpleBackground,
        imageOrientation === 'landscape' && styles.landscapeImage,
        imageSide === 'right' && styles.imageOnRight,
      )}
      contentClassName={styles.contentWrapper}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
    >
      <div className={clsx(styles.contentContainer)}>
        <div className={clsx(styles.mediaContainer)}>
          <div className={clsx(styles.imageContainer)}>
            <Image image={image} className={styles.image} />
            <Arc
              borderSide={imageSide === 'right' ? 'right' : 'left'}
              className={styles.arc}
              showOnlyInDesktop
            />
            {imageOrientation === 'portrait' && addCompanyLogoToImage && (
              <img
                className={styles.companyLogo}
                alt=""
                src={backgroundColor === '#E5F8E8' ? companyLogo_green : companyLogo_purple}
              />
            )}
          </div>
        </div>
        <div className={styles.textContainer}>
          {title && <h2 className={styles.title}>{title}</h2>}
          {text && <GenericPortableText blocks={text} className={styles.text} />}
          {fileRows &&
            fileRows.length > 0 &&
            fileRows.map(
              (fileRow, index) =>
                !!(fileRow.file?.asset || fileRow.link) && (
                  <FileRow
                    key={index}
                    title={fileRow.name}
                    description={fileRow.description}
                    size={fileRow.file?.asset.size}
                    fileUrl={fileRow.file?.asset.url}
                    link={fileRow.link}
                    onDownloadClick={openModalIfTermsNotAccepted}
                    className={styles.fileRow}
                  ></FileRow>
                ),
            )}
        </div>
      </div>
    </ModuleLayout>
  );
}

export default TextAndImageModule;
